/**
 * Vue Store related to authentication.
 *
 * @author Documill 2024
 */

import apiCall from './../../utils/api';
import { logger } from '../../utils/logger';
import LocalStorage from './../../utils/localStorage';

export default {

  state: {
    /**
     * User token to be used for API Call
     */
    token: '',

    /**
     * Authentication progress status
     * <loading | success | error>
     */
    status: '',

    organizationId: '',
  },

  getters: {
    /**
     * Returns value to determine if user is authenticated
     * if there is a Token
     */
    isAuthenticated: state => !!state.token,

    /**
     * Returns value of authentication status.
     */
    authStatus: state => state.status,

    organizationId: state =>state.organizationId,
  },

  mutations: {
    /**
     * Marks the beginning of authentication process
     */
    AUTH_REQUEST(state) {
      state.status = 'loading';
    },
    /**
     * Marks the success of authentication process
     * Saves Token to Vue Store
     */
    AUTH_SUCCESS(state, token) {
      state.status = 'success';
      state.token =token;
    },

    ORGANIZATION_SIGN_IN(state, organizationId) {
      state.organizationId = organizationId;
    },

    /**
     * Marks the failure of authentication process
     */
    AUTH_ERROR(state) {
      state.status = 'error';
    },
    /**
     * Removes Token from Vue Store
     */
    AUTH_LOGOUT(state) {
      state.status = '';
      state.token = '';
      state.organizationId='';
    },
  },

  actions: {
    async loadUserInformation(context) {
      let token = LocalStorage.getUserToken();

      if(!token) {
        return {
          state: "NOT_LOGGED_IN",
        };
      }

      apiCall.defaults.headers.common['Authorization'] = token;

      return await context.dispatch("getUserInfo").then(result => {
        // User was recognized
        context.commit("AUTH_SUCCESS",token);

        return {
          state: "LOGGED_IN"
        };
      })
      .catch(error => {
        if(error.response &&
          (error.response.status == 401 || error.response.status == 403)) {

          logger.warn("User token invalid or expired, need to log in again")
          context.dispatch("removeUserToken");
          return {
            state: "NOT_LOGGED_IN",
          };
        } else {
          // Should occur only if backend is down or broken
          logger.error("Failed to contact server",error);
          throw error;
        }
      });
    },



    async checkForLoginToken(context) {

      // Already logged in.
      if(!this.getters.organizationId == "" || this.getters.isAuthenticated)
        return false;

      let loginToken = new URL(window.location.href).searchParams.get('login-token');

      if(loginToken) { // New login detected

        let loginData = {
          "loginToken" : loginToken
        }
        // The method of authentication depends on whether we have a registration or an app token.
        let loginEndpoint = "/v1/users/sign-only-login";

        return logIn(loginEndpoint,loginData,this);
      }
      else {
        return false;
      }

      // --- sub-methods ---

      async function logIn(loginEndpoint,loginData,vueInstance) {

        return apiCall.post(loginEndpoint,loginData)
        .then(result => {
          let loginResult = result.data;

          vueInstance.commit('ORGANIZATION_SIGN_IN', loginResult.organizationId);
          LocalStorage.setUserToken(loginResult.token);
          return true; // Handled login
        })
        .catch(error => {
           logger.error("Login failed");
          return "ERROR";
        });
      }

    },

    removeUserToken(context) {
      LocalStorage.clear();
      context.commit("AUTH_LOGOUT");
    },

    async redirectToLogin(context, params) {

      let organizationId = params.organizationId;
      let loginUrl

      // Direct the user to organization home or the login pages in flow-website depending whether
      // the organizationId route parameter is defined.

      // Note: We cannot validate at this point "organizationId" value since the applicable
      //       API endpoint to get the public information requires authorization token of the user.
      // Note: Flow-website seems to throw error when the "organizationId" is not an exisiting
      //       organization identity.

      if(organizationId)
        loginUrl= window.FlowSignOnlyWebsiteGlobalConfig.basePaths.flowWebsite +"/" +organizationId +"/home";
      else
        loginUrl= window.FlowSignOnlyWebsiteGlobalConfig.basePaths.flowWebsite +"/login";


      logger.info("Redirecting to login, then coming back to",
                  sessionStorage.getItem("soflowPageBeforeLogin"));

      window.location.href = loginUrl;
    },

    /**
     * Logout a user and remove all user/organization tokens from LocalStorage.
     */
    async authLogout(context) {
      await context.dispatch('logout');

      LocalStorage.clear();
    },

    /**
     * Logout user by:
     * - Calling logout in backend to clear tokens from database
     * - Removing API call header for authorization
     * - Removing the Token from Vuex Store
     * - Removing user and organization info from Vuex Store
     *
     * NOTE: This method doesn't remove tokens from LocalStorage!
     *       The tokens that need to be removed depend on the logout
     *       (see authLogout(), authLogoutOrg)
     */
    async logout(context) {
      function clearSensitiveData() {
        delete apiCall.defaults.headers.common['Authorization'];
        sessionStorage.removeItem("flowPageBeforeLogin");
        // Removing the Token from Vue Store
        context.commit('AUTH_LOGOUT');
        context.dispatch('clearUserInfo');
        // TODO: clearOrgInfo
        //context.dispatch("clearOrgInfo");
      }

      // The user may already be logged out in another browser tab (DOS-3739)
      if(!LocalStorage.getUserToken())
        return; // Do nothing

      try {
        // Calling logout in backend to clear token from database
        const result = await apiCall.post('/v1/users/logout');

        clearSensitiveData();
        logger.debug("Query logout succeeded with result:", result);
      } catch(error) {
        context.commit('AUTH_ERROR', error);
        // if the request fails, remove any possible user token if possible
        clearSensitiveData();
        logger.error("Query logout didn't succeed with message:", error);
      }
    },

    /**
     * Loads Guest user information and accepts invitation.
     *
     * TODO: This method is named a bit weirdly. The "accept invitation" seems to be the
     *       main action.
     *
     * Expected parameters:
     * {
     *   invitationToken: String,
     *   taskId: String (optional)
     * }
     *
     * @param {*} context
     * @param {*} params
     * @returns
     */
    async loadGuestUserInformation(context,params) {
      if(context.getters.isAuthenticated) {
        return;
      }

      let url = '/v1/invitation/' + params.invitationToken + '/guest';
      if(params.taskId != null)
        url += '/' + params.taskId;

      return await apiCall.get(url)
        .then(result => {
          const projectId = result.data.projectId;
          const collaboratorId = result.data.collaboratorId;

          logger.debug("Accepted guest invitation for project: " + projectId);

          if(!collaboratorId)
            console.error("Did not get collaborator id for guest");

          // Set user token that will be read by loadUserInformation when called by router.js,
          // which will pass it to getUserInfo
          // The getUserInfo will also receive collaborator id again, and store it.

          LocalStorage.setUserToken(result.data.guestToken);

          return result.data;
        });
    },
  }
}
